import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  InputAdornment,
  OutlinedInput,
  MenuItem,
  Alert
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import moment from "moment";
import RadioGenerator from "components/shared/RadioGenerator/RadioGenerator";
import Button from "components/shared/Button/Button";
import LocalHospitalOutlinedIcon from "@mui/icons-material/HealthAndSafety";
import {
  AddEncounterEvent,
  dayArr,
  monthArr,
  monthTranslate,
  yearArr
} from "helpers/variables";
import {
  StyledContainer,
  StyledHeader,
  StyledTextField,
  StyledStack,
  StyledAdornment,
  StyledFormControlLabel,
  StyledTypography,
  StyledHeadTypography,
  StyledMultiLineTypography,
  StyledOutlinedInput,
  StyledTypographyIcon
} from "./Details.styles";
import { GlobalContext } from "context/Provider";
import API from "api";
import Session from "helpers/session";
import { useTranslation } from "react-i18next";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";

interface State {
  inches: number;
  feet: number;
  general: string;
  glucose: string;
}

interface DetailProps {
  type: "coordinator" | "member";
  action: "create" | "edit";
}

export default function Details(props: DetailProps) {
  const { t } = useTranslation();
  const { member, memberDispatch, encounterDispatch, encounters } =
    useContext(GlobalContext);
  const current = member?.current;
  let dressedweight: string | number = 0;

  const [showSuccess, setSuccess] = useState<string>("");

  const [units, setUnits] = React.useState<State>({
    inches: current?.height?.inches || 0,
    feet: current?.height?.feet || 0,
    general: current?.units?.general || "",
    glucose: current?.units?.glucose || ""
  });

  const [isDisabled, setChanged] = useState<boolean>(true);

  const [memberChanges, setMemberChanges] = useState({
    birthDate: current?.birthDate || "",
    gender: current?.gender || "",
    language: current?.language || "",
    condition: current?.condition || "",
    estimatedGraduation: current?.estimatedGraduation || "",
    units: {
      general: current?.units?.general || "",
      glucose: current?.units?.glucose || ""
    },
    height: {
      feet: current?.height?.feet || 0,
      inches: current?.height?.inches || 0
    },
    weight: current?.weight || 0,
    status: current?.status || ""
  });

  const inputValidation = (changes: any) => {
    if (props.type === "member") {
      if (changes.feet < 0 && changes.inches < 0) {
        changes.feet = 0;
        changes.inches = 0;
        alert(
          "These values do not exist within the expected limits for height. "
        );
      } else if (changes.feet < 0) {
        changes.feet = 0;
        alert(
          "These values do not exist within the expected limits for height."
        );
      } else if (changes.inches < 0) {
        changes.inches = 0;
        alert(
          "These values do not exist within the expected limits for height."
        );
      } else {
        if (parseInt(changes.feet.toString()) > 9 && changes.inches >= 12) {
          changes.feet = 0;
          changes.inches = 0;
          alert(
            "These values do not exist within the expected limits for height."
          );
        } else if (parseInt(changes.feet.toString()) > 9) {
          changes.feet = 0;
          alert(
            "These values do not exist within the expected limits for height."
          );
        } else if (changes.inches >= 12) {
          changes.inches = 0;
          alert(
            "These values do not exist within the expected limits for height."
          );
        }
      }
      changes.inches = parseInt(changes.inches.toString().slice(0, 4));
      let stringifiedWeight = memberChanges.weight.toString();
      if (memberChanges.weight < 1400 || memberChanges.weight > 0) {
        if (stringifiedWeight.includes(".")) {
          dressedweight = stringifiedWeight.slice(
            0,
            stringifiedWeight.indexOf(".") + 3
          );
        } else {
          dressedweight = stringifiedWeight;
        }
      } else {
        alert("invalid weight");
        dressedweight = 0;
      }
    }
  };

  const updateMember = () => {
    let changes = units;
    inputValidation(changes);
    const memberUpdates = {
      ...memberChanges,
      height: {
        feet: parseInt(changes.feet.toString()).toFixed(2),
        inches: changes.inches
      },
      units: { general: units.general, glucose: units.glucose },
      birthDate: `${dobYear}${monthTranslate(dobMonth)}${dobDay}`,
      estimatedGraduation: `${monthTranslate(
        gradMonth
      )}/${gradDay}/${gradYear}`,
      weight: dressedweight,
      status: memberChanges.status
    };
    //TODO: change to toggle between token once provider auth is completed
    // Upsert Call
    let userCall = API.kore.members.upsert(
      props.action === "create" ? "create" : "edit",
      memberUpdates,
      Session.crackCookieToken("coordinator"),
      member?.current.id
    );
    setSuccess("success");
    if (props.type === "coordinator") {
      userCall = API.kore.coordinator.upsert(
        props.action === "create" ? "create" : "edit",
        memberUpdates,
        Session.crackCookieToken("coordinator"),
        member?.current.id
      );
    }
    userCall.then((updatedMember: any) => {
      setChanged(true);
      // Dispatch Set
      if (props.type === "member") {
        memberDispatch({
          type: "CHANGE_TARGET_MEMBER",
          payload: {
            id: updatedMember.id,
            email: updatedMember.email,
            externId: updatedMember.externId,
            firstName: updatedMember.firstName,
            lastName: updatedMember.lastName,
            username: updatedMember.username,
            program: updatedMember.program,
            phone: updatedMember.phone,
            groups: updatedMember.groups,
            site: updatedMember.site,
            birthDate: memberUpdates.birthDate,
            gender: memberUpdates.gender,
            language: memberUpdates.language,
            conditon: memberUpdates.condition,
            estimatedGraduation: memberUpdates.estimatedGraduation,
            height: memberUpdates.height,
            weight: memberUpdates.weight,
            status: memberUpdates.status,
            units: memberUpdates.units,
            locked: updatedMember.locked,
            latestReading: updatedMember.latestReading,
            mostRecentReading: updatedMember.mostRecentReading
          }
        });
      }
    });
    const encounterEvent = AddEncounterEvent(
      "profile change",
      "Management details updated"
    );
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("encounter") || "",
      encounterEvent,
      member?.secondsCounter.toString() || ""
    );
  };

  // Handle changes
  const handleMemberChange = (event: any) => {
    setChanged(false);
    setSuccess("");
    setMemberChanges({
      ...memberChanges,
      [event.target.id]: event.target.value
    });
  };

  const handleInputChange =
    (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setChanged(false);
      setSuccess("");
      const value = event.target.value.indexOf("/")
        ? event.target.value.split("/")[0]
        : event.target.value;
      setUnits({ ...units, [prop]: value });
    };

  // Date Of Birth
  const [dobYear, setDobYear] = useState("Year");
  const handleDobYearChange = (event: SelectChangeEvent) => {
    setChanged(false);
    setDobYear(event.target.value as string);
  };
  const [dobMonth, setDobMonth] = useState("Month");
  const handleDobMonthChange = (event: SelectChangeEvent) => {
    setChanged(false);
    setDobMonth(event.target.value as string);
  };
  const [dobDay, setDobDay] = useState("Day");
  const handleDobDayChange = (event: SelectChangeEvent) => {
    setChanged(false);
    setDobDay(event.target.value as string);
  };
  // Estimated Graduation !!!!
  const [gradYear, setGradYear] = useState("Year");
  const handleGradYearChange = (event: SelectChangeEvent) => {
    setChanged(false);
    setGradYear(event.target.value as string);
  };
  const [gradMonth, setGradMonth] = useState("Month");
  const handleGradMonthChange = (event: SelectChangeEvent) => {
    setChanged(false);
    setGradMonth(event.target.value as string);
  };
  const [gradDay, setGradDay] = useState("Day");
  const handleGradDayChange = (event: SelectChangeEvent) => {
    setChanged(false);
    setGradDay(event.target.value as string);
  };

  const generateDateSelects = () => {
    if (member?.current.birthDate === "YearMonthDay") {
      setDobDay("Day");
      setDobMonth("Month");
      setDobYear("Year");
    } else {
      const birthDate = member?.current.birthDate;

      if (!moment(birthDate, "YYYY-MM-DD").isValid()) {
        setDobDay("Day");
        setDobMonth("Month");
        setDobYear("Year");
      } else {
        const dateObj = moment(birthDate, "YYYY-MM-DD");
        setDobDay(dateObj.format("D"));
        setDobMonth(dateObj.format("MMMM"));
        setDobYear(dateObj.format("YYYY"));
      }
    }

    if (member?.current.estimatedGraduation === "YearMonthDay") {
      setGradDay("Day");
      setGradMonth("Month");
      setGradYear("Year");
    } else {
      const estimatedGraduation = member?.current.estimatedGraduation;
      if (!moment(estimatedGraduation, "YYYY-MM-DD").isValid()) {
        setGradDay("Day");
        setGradMonth("Month");
        setGradYear("Year");
      } else {
        const dateObj = moment(estimatedGraduation, "YYYY-MM-DD");
        setGradDay(dateObj.format("D"));
        setGradMonth(dateObj.format("MMMM"));
        setGradYear(dateObj.format("YYYY"));
      }
    }
  };
  // Use Effect
  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    if (!member?.current.id) {
      API.kore.members
        .getOne(
          window.location.pathname.split("/")[3],
          Session.crackCookieToken("coordinator")
        )
        .then((ret) => {
          memberDispatch({ type: "CHANGE_TARGET_MEMBER", payload: ret });
          setUnits({
            inches: ret.height?.inches || 0,
            feet: ret.height?.feet || 0,
            general: ret.units?.general || "",
            glucose: ret.units?.glucose || ""
          });
          setMemberChanges({
            birthDate: ret.birthDate || "",
            estimatedGraduation: ret.estimatedGraduation || "",
            gender: ret.gender || "",
            language: ret.language || "",
            condition: ret.condition || "",
            units: {
              general: ret.units?.general || "",
              glucose: ret.units?.glucose || ""
            },
            height: {
              feet: ret.height?.feet || "",
              inches: ret.height?.inches || ""
            },
            weight: ret.weight || 0,
            status: ret.status || ""
          });
        });
    }
    generateDateSelects();
  }, []);

  return (
    <StyledContainer>
      <StyledHeader>
        <h2>{t("u-management-details")}</h2>
      </StyledHeader>
      <EncounterTimer></EncounterTimer>
      <Grid container>
        <Grid item md={4} lg={3}>
          <StyledHeadTypography>{t("u-date-of-birth")}</StyledHeadTypography>
          <StyledTypography>{t("u-gender")}</StyledTypography>
          <StyledTypography>{t("u-preferred-language")}</StyledTypography>
          <StyledMultiLineTypography>
            {t("u-condition")}
          </StyledMultiLineTypography>
          <StyledHeadTypography>
            {t("Estimated Graduation Date")}
          </StyledHeadTypography>
          <StyledTypography>{t("u-units")}</StyledTypography>
          <StyledTypography>{t("u-glucose")}</StyledTypography>
          <StyledTypography>{t("u-height")}</StyledTypography>
          <StyledTypography>Goal Weight</StyledTypography>
          <StyledTypographyIcon>
            <LocalHospitalOutlinedIcon />
            {t("u-admission-status")}
          </StyledTypographyIcon>
        </Grid>
        <Grid item xs={6}>
          <StyledStack spacing={1}>
            <StyledStack spacing={1} direction="row">
              <Select
                value={dobMonth}
                onChange={handleDobMonthChange}
                fullWidth
              >
                {monthArr.map((value) => {
                  return (
                    <MenuItem key={`dob-month-${value}`} value={value}>
                      {value}
                    </MenuItem>
                  );
                })}
              </Select>
              <Select value={dobDay} onChange={handleDobDayChange} fullWidth>
                {dayArr
                  .map((value) => {
                    return (
                      <MenuItem key={`dob-day-${value}`} value={value}>
                        {value}
                      </MenuItem>
                    );
                  })
                  .reverse()}
              </Select>
              <Select value={dobYear} onChange={handleDobYearChange} fullWidth>
                {yearArr.map((value) => {
                  return (
                    <MenuItem key={`dob-year-${value}`} value={value}>
                      {value}
                    </MenuItem>
                  );
                })}
              </Select>
            </StyledStack>
            <StyledFormControlLabel
              label=""
              control={
                <RadioGenerator
                  id="gender"
                  inputArray={[t("u-female"), t("u-male"), t("u-other")]}
                  value={memberChanges.gender}
                  onChange={handleMemberChange}
                />
              }
              labelPlacement="start"
            />
            <StyledFormControlLabel
              label=""
              control={
                <StyledTextField
                  id="language"
                  placeholder={t("u-english")}
                  value={memberChanges.language}
                  onChange={handleMemberChange}
                />
              }
              labelPlacement="start"
            />
            <StyledFormControlLabel
              label=""
              control={
                <StyledTextField
                  id="condition"
                  placeholder={t("u-condition")}
                  multiline
                  minRows={4}
                  maxRows={4}
                  value={memberChanges.condition}
                  onChange={handleMemberChange}
                />
              }
              labelPlacement="start"
            />
            <StyledStack spacing={1} direction="row">
              <Select
                value={gradMonth}
                onChange={handleGradMonthChange}
                fullWidth
              >
                {monthArr.map((value) => {
                  return (
                    <MenuItem
                      key={`graduation-period-month-${value}`}
                      value={value}
                    >
                      {value}
                    </MenuItem>
                  );
                })}
              </Select>
              <Select value={gradDay} onChange={handleGradDayChange} fullWidth>
                {dayArr
                  .map((value) => {
                    return (
                      <MenuItem
                        key={`graduation-period-day-${value}`}
                        value={value}
                      >
                        {value}
                      </MenuItem>
                    );
                  })
                  .reverse()}
              </Select>
              <Select
                value={gradYear}
                onChange={handleGradYearChange}
                fullWidth
              >
                {yearArr.map((value) => {
                  return (
                    <MenuItem
                      key={`graduation-period-year-${value}`}
                      value={value}
                    >
                      {value}
                    </MenuItem>
                  );
                })}
              </Select>
            </StyledStack>
            <StyledFormControlLabel
              label=""
              control={
                <RadioGenerator
                  id="general"
                  inputArray={[t("u-metric"), t("u-imperial")]}
                  value={units.general}
                  onChange={handleInputChange("general")}
                />
              }
              labelPlacement="start"
            />
            <StyledFormControlLabel
              label=""
              control={
                <RadioGenerator
                  id="glucose"
                  inputArray={["mg/dL", "mmol/L"]}
                  value={units.glucose === "mg" ? "mg/dL" : "mmol/L"}
                  onChange={handleInputChange("glucose")}
                />
              }
              labelPlacement="start"
            />
            <StyledStack spacing={5} direction="row">
              <OutlinedInput
                value={units.feet}
                type="number"
                onChange={handleInputChange("feet")}
                endAdornment={
                  <InputAdornment position="end">ft.</InputAdornment>
                }
              />
              <OutlinedInput
                value={units.inches}
                type="number"
                onChange={handleInputChange("inches")}
                endAdornment={
                  <InputAdornment position="end" variant="filled">
                    in.
                  </InputAdornment>
                }
              />
            </StyledStack>
            <StyledOutlinedInput
              id="weight"
              value={memberChanges.weight}
              type="number"
              onChange={handleMemberChange}
              endAdornment={
                <StyledAdornment position="end">lbs.</StyledAdornment>
              }
            />
            <StyledFormControlLabel
              label=""
              control={
                <RadioGenerator
                  id="status"
                  inputArray={[t("u-none"), t("u-admitted")]}
                  value={memberChanges.status}
                />
              }
              onChange={handleMemberChange}
              labelPlacement="start"
            />
            <Button
              variant="success"
              disabled={isDisabled}
              onClick={updateMember}
            >
              {t("u-save")}
            </Button>
            {showSuccess === "success" ? (
              <Alert severity="success">
                Your configuration changes have been saved
              </Alert>
            ) : (
              ""
            )}
          </StyledStack>
        </Grid>
      </Grid>
    </StyledContainer>
  );
}
